// components/ChallengeModal.jsx
import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';

function ChallengeModal({ challenge, onClose, onSave, challengePool }) {
  const [positive, setPositive] = useState(challenge?.positive || '');
  const [negative, setNegative] = useState(challenge?.negative || '');
  const [category, setCategory] = useState(challenge?.category || (challengePool[0]?.category || ''));
  const [challengeWeight, setChallengeWeight] = useState(challenge?.weight || 1);
  const [durationPositive, setDurationPositive] = useState(challenge?.durationPositive ?? '');
  const [durationNegative, setDurationNegative] = useState(challenge?.durationNegative ?? '');

  const existingCategories = challengePool.map(cat => cat.category);
  const isNewChallenge = !challenge?.id;

  // Reset form fields when challenge prop changes (e.g., when opening for a new challenge)
  useEffect(() => {
    if (challenge) {
      setPositive(challenge.positive || '');
      setNegative(challenge.negative || '');
      setChallengeWeight(challenge.weight || 1);
      setDurationPositive(challenge.durationPositive ?? '');
      setDurationNegative(challenge.durationNegative ?? '');
      setCategory(challenge.category || (challengePool[0]?.category || ''));
    } else {
      setPositive('');
      setNegative('');
      setChallengeWeight(1);
      setDurationPositive('');
      setDurationNegative('');
      setCategory(challengePool[0]?.category || '');
    }
  }, [challenge, challengePool]);


  const handleSubmit = (e) => {
    e.preventDefault();
    if (!category || !positive.trim() || !negative.trim()) {
      alert('Bitte alle Felder ausfüllen (Kategorie, Positive und Negative Wirkung)!');
      return;
    }
    onSave(challenge?.category || category, category, {
      id: challenge?.id || undefined,
      positive,
      negative,
      weight: challengeWeight,
      durationPositive: durationPositive === '' ? undefined : Number(durationPositive),
      durationNegative: durationNegative === '' ? undefined : Number(durationNegative)
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4">
      <div className="bg-surface rounded-xl shadow-2xl p-8 w-full max-w-lg text-text border border-border relative">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors"
          title="Schließen"
        >
          <FontAwesomeIcon icon={faTimes} size="lg" />
        </button>
        <h2 className="text-2xl font-bold mb-6 text-center">
          {isNewChallenge ? 'Neue Challenge hinzufügen' : 'Challenge bearbeiten'}
        </h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Category Selection */}
          <div>
            <label htmlFor="modal-category" className="block text-text-light text-sm font-bold mb-2">Kategorie</label>
            <select
              id="modal-category"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              required
              className="shadow appearance-none border rounded w-full py-2 px-3 text-text-light leading-tight focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent bg-background"
            >
              <option value="">Kategorie wählen</option>
              {existingCategories.map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          </div>

          {/* Weight */}
          <div className="mb-4">
            <label className="block text-sm font-bold text-text-light mb-1">Wertigkeit</label>
            <input
              type="number"
              min={1}
              value={challengeWeight}
              onChange={(e) => setChallengeWeight(Number(e.target.value))}
              className="w-full rounded border border-border px-3 py-2 bg-background text-text-light"
            />
          </div>

          {/* Positive Effect */}
          <div>
            <label htmlFor="modal-positive" className="block text-text-light text-sm font-bold mb-2">Positive Wirkung</label>
            <textarea
              id="modal-positive"
              value={positive}
              onChange={(e) => setPositive(e.target.value)}
              required
              className="shadow appearance-none border rounded w-full py-2 px-3 text-text-light leading-tight focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent bg-background resize-y"
              rows="3"
            ></textarea>
          </div>

          {/* Duration Positive */}
          <div className="mb-4">
            <label className="block text-sm font-bold text-text-light mb-1">Dauer (positiv, in Phasen)</label>
            <input
              type="number"
              min={0}
              value={durationPositive}
              onChange={(e) => setDurationPositive(e.target.value)}
              placeholder="Leer = unendlich"
              className="w-full rounded border border-border px-3 py-2 bg-background text-text-light"
            />
          </div>

          {/* Negative Effect */}
          <div>
            <label htmlFor="modal-negative" className="block text-text-light text-sm font-bold mb-2">Negative Wirkung</label>
            <textarea
              id="modal-negative"
              value={negative}
              onChange={(e) => setNegative(e.target.value)}
              required
              className="shadow appearance-none border rounded w-full py-2 px-3 text-text-light leading-tight focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent bg-background resize-y"
              rows="3"
            ></textarea>
          </div>

          {/* Duration Negative */}
          <div className="mb-4">
            <label className="block text-sm font-bold text-text-light mb-1">Dauer (negativ, in Phasen)</label>
            <input
              type="number"
              min={0}
              value={durationNegative}
              onChange={(e) => setDurationNegative(e.target.value)}
              placeholder="Leer = unendlich"
              className="w-full rounded border border-border px-3 py-2 bg-background text-text-light"
            />
          </div>

          {/* Buttons */}
          <div className="flex justify-end gap-3 mt-6">
            <button
              type="button"
              onClick={onClose}
              className="bg-gray-300 hover:bg-gray-400 text-gray-800 font-bold py-2 px-4 rounded-lg shadow-md transition-colors duration-200"
            >
              Abbrechen
            </button>
            <button
              type="submit"
              className="bg-primary hover:bg-primary-dark text-white font-bold py-2 px-4 rounded-lg shadow-md transition-colors duration-200"
            >
              Speichern
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default ChallengeModal;